import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Copy, Download, RefreshCw } from 'lucide-react';
import { useMemo } from 'react';

import { BRAND } from '@/components/Brand';
import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import { Input } from '@/design-system/ui/Input';
import { buildIcs, memberCalendarItems } from '@escala/domain/calendar';
import { useStore } from '@/store/AppStore';

export const CALENDAR_FEED_KEY = ['calendar-feed'] as const;

/** Baixa o texto como arquivo (o .ics que a agenda importa). */
function download(text: string, filename: string): boolean {
  if (typeof URL.createObjectURL !== 'function') return false;

  const url = URL.createObjectURL(new Blob([text], { type: 'text/calendar;charset=utf-8' }));
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);

  return true;
}

/**
 * "Sincronizar agenda": as escalas do membro no Google Agenda, no celular (Apple) ou no
 * Outlook — um link secreto que o aplicativo de agenda assina e busca sozinho, e a agenda
 * acompanha as mudanças da escala; e o arquivo .ics (uma cópia de agora, que não se
 * atualiza).
 */
export default function CalendarSyncDialog({ onClose }: { onClose: () => void }) {
  const { state, lookup, me, today, calendar, actions } = useStore();
  const queryClient = useQueryClient();
  const feed = useQuery({ queryKey: CALENDAR_FEED_KEY, queryFn: calendar.feed });
  const rotate = useMutation({
    mutationFn: calendar.rotate,
    onSuccess: (fresh) => {
      queryClient.setQueryData(CALENDAR_FEED_KEY, fresh);
      actions.showToast('Link novo gerado. O anterior parou de funcionar: assine de novo nas suas agendas.');
    },
    onError: () => actions.showToast('Não foi possível gerar outro link agora.'),
  });
  const items = useMemo(
    () => memberCalendarItems(state.events, me.id, lookup, today),
    [lookup, me.id, state, today]
  );
  const links = feed.data ?? null;

  const copy = () => {
    if (!links) return;

    const fail = () => actions.showToast('Não foi possível copiar. Selecione o link e copie.');

    if (!navigator.clipboard?.writeText) return fail();
    navigator.clipboard
      .writeText(links.url)
      .then(() => actions.showToast('Link copiado. Cole em “Adicionar agenda por URL”.'), fail);
  };

  const downloadIcs = () => {
    const ok = download(buildIcs(items, { name: `${BRAND.full} · ${me.name}` }), 'minhas-escalas.ics');

    actions.showToast(
      ok
        ? 'Arquivo baixado. Abra-o para importar as escalas na sua agenda.'
        : 'Não foi possível baixar o arquivo.'
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(540px,calc(100vw-24px))]">
        <DialogHeader
          title="Sincronizar agenda"
          description="Suas escalas no Google Agenda, Apple ou Outlook, sempre atualizadas."
        />
        <DialogBody>
          <section aria-label="Link da agenda" className="flex flex-col gap-2.5">
            <p className="m-0 text-small text-ink-secondary">
              Assine este link uma vez: escalas novas, trocas e cancelamentos aparecem sozinhos. Só entram as
              escalas publicadas.
            </p>
            {feed.isError ? (
              <Alert tone="danger">
                Não foi possível carregar o seu link agora. Tente de novo mais tarde.
              </Alert>
            ) : (
              <div className="flex gap-2">
                <label className="min-w-0 flex-1">
                  <span className="sr-only">Link da sua agenda</span>
                  <Input
                    readOnly
                    value={links?.url ?? 'Carregando…'}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                </label>
                <Button variant="secondary" onClick={copy} disabled={!links}>
                  <Copy aria-hidden />
                  Copiar
                </Button>
              </div>
            )}
            {links ? (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
                <Button asChild variant="soft">
                  <a href={links.google} target="_blank" rel="noopener noreferrer">
                    <CalendarDays aria-hidden />
                    Google Agenda
                  </a>
                </Button>
                <Button asChild variant="soft">
                  <a href={links.webcal}>
                    <CalendarDays aria-hidden />
                    Apple / celular
                  </a>
                </Button>
                <Button asChild variant="soft">
                  <a href={links.outlook} target="_blank" rel="noopener noreferrer">
                    <CalendarDays aria-hidden />
                    Outlook
                  </a>
                </Button>
              </div>
            ) : null}
            <p className="m-0 text-meta text-ink-subtle">
              O Google Agenda pode levar algumas horas para buscar mudanças; o celular e o Outlook costumam
              ser mais rápidos. Quem tem o link vê as suas escalas: se ele foi compartilhado sem querer, gere
              outro.
            </p>
          </section>

          <section
            aria-label="Arquivo da agenda"
            className="flex flex-col gap-1.5 border-t border-line-divider pt-3.5"
          >
            <p className="m-0 text-control font-semibold">Arquivo .ics</p>
            <p className="m-0 text-small text-ink-muted">
              {items.length
                ? `${items.length} ${items.length === 1 ? 'escala publicada' : 'escalas publicadas'} de agora, para importar uma vez (não se atualiza).`
                : 'Você ainda não tem escalas publicadas.'}
            </p>
          </section>
        </DialogBody>
        <DialogFooter className="items-center">
          <Button variant="ghost" onClick={() => rotate.mutate()} disabled={!links || rotate.isPending}>
            <RefreshCw aria-hidden />
            Gerar outro link
          </Button>
          <span className="flex-1" />
          <Button variant="secondary" onClick={downloadIcs} disabled={!items.length}>
            <Download aria-hidden />
            Baixar .ics
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
